import { useCallback, useRef, useState } from 'react';
import { Hash, Search } from 'lucide-react';
import { AutoComplete, type AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonButton from '../../../../components/common-components/button/CommonButton';
import Popup from '../../../../components/common-components/pop-up/Popup';
import { useStore } from '../../../../hooks/useStore';
import type { ITagSearchResult } from '../../../../models/hospital/HospitalModel';
import { hospitalService } from '../../../../services/modules/hospital/hospital.service';
import styles from '../HospitalManagement.module.css';

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

const SearchTagByNumberPopup = () => {
  const { popupStore, hospitalFilesStore } = useStore();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<ITagSearchResult[]>([]);
  const [selectedTag, setSelectedTag] = useState<ITagSearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleClose = () => {
    popupStore.setIsSearchTagByNumberOpen(false);
    setQuery('');
    setSuggestions([]);
    setSelectedTag(null);
    setLoading(false);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  };

  const fetchSuggestions = useCallback(async (searchQuery: string) => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await hospitalService.searchTagsByPrefix(trimmed);
      setSuggestions(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Etiket arama hatası:', error);
      toast.error('Etiket aranırken bir hata oluştu');
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleComplete = (event: AutoCompleteCompleteEvent) => {
    const nextQuery = event.query;
    setQuery(nextQuery);
    setSelectedTag(null);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (nextQuery.trim().length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(nextQuery);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleSelect = (item: ITagSearchResult) => {
    if (!item?.hospitalId || !item?.id) {
      toast.error('Etiket bilgisi bulunamadı');
      return;
    }

    hospitalFilesStore.setSelectedHospital({
      id: item.hospitalId,
      hospitalName: item.hospitalName,
    });
    hospitalFilesStore.setSelectedTag({
      id: item.id,
      prefix: item.prefix,
      sequence: 0,
    });

    navigate('/hospital-files');
    handleClose();
  };

  const handleNavigate = () => {
    if (!selectedTag) {
      toast.error('Lütfen bir etiket seçin');
      return;
    }
    handleSelect(selectedTag);
  };

  const itemTemplate = (item: ITagSearchResult) => (
    <div className={styles.tagSearchItem}>
      <span className={styles.tagSearchPrefix}>{item.prefix}</span>
      <span className={styles.tagSearchHospital}>{item.hospitalName}</span>
    </div>
  );

  return (
    <Popup
      isOpen={popupStore.isSearchTagByNumberOpen}
      onClose={handleClose}
      title="Etiket No ile Ara"
      description="Etiket numarasını yazın ve eşleşen sonuçtan hastaneye gidin"
      variant="info"
      icon={<Search size={22} strokeWidth={2} />}
    >
      <div className={styles.tagSearchContainer}>
        <label className={styles.tagSearchLabel} htmlFor="tag-search-autocomplete">
          Etiket No
        </label>
        <span className={styles.tagSearchInputWrap}>
          <Hash size={18} strokeWidth={2} className={styles.tagSearchInputIcon} aria-hidden />
          <AutoComplete
            inputId="tag-search-autocomplete"
            value={selectedTag ?? query}
            suggestions={suggestions}
            completeMethod={handleComplete}
            field="prefix"
            placeholder="En az 2 karakter girin..."
            itemTemplate={itemTemplate}
            className={styles.tagSearchAutocomplete}
            inputClassName={styles.tagSearchInput}
            panelClassName={styles.tagSearchPanel}
            panelStyle={{ zIndex: 1200 }}
            appendTo={typeof document !== 'undefined' ? document.body : undefined}
            forceSelection={false}
            dropdown={false}
            minLength={MIN_QUERY_LENGTH}
            onChange={e => {
              const nextValue = e.value;
              if (typeof nextValue === 'string') {
                setQuery(nextValue);
                setSelectedTag(null);
                return;
              }
              if (nextValue && typeof nextValue === 'object') {
                setSelectedTag(nextValue as ITagSearchResult);
                setQuery((nextValue as ITagSearchResult).prefix);
              }
            }}
            onSelect={e => {
              const item = e.value as ITagSearchResult;
              handleSelect(item);
            }}
            emptyMessage={
              loading
                ? 'Aranıyor...'
                : query.trim().length < MIN_QUERY_LENGTH
                  ? `En az ${MIN_QUERY_LENGTH} karakter girin`
                  : 'Eşleşen etiket bulunamadı'
            }
          />
        </span>

        {selectedTag && (
          <div className={styles.tagSearchSelectedInfo}>
            <strong>{selectedTag.prefix}</strong>
            <span>{selectedTag.hospitalName}</span>
          </div>
        )}

        <div className={styles.tagSearchActions}>
          <CommonButton type="secondary" onClick={handleClose}>
            İptal
          </CommonButton>
          <CommonButton type="primary" onClick={handleNavigate} disabled={!selectedTag || loading}>
            Git
          </CommonButton>
        </div>
      </div>
    </Popup>
  );
};

export default observer(SearchTagByNumberPopup);
